import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {Badge, Button, Form, Input} from 'antd';
import {FormListFieldData} from 'antd/es/form/FormList';
import React, {FC, memo} from 'react';

import {EComparisonType} from 'components/form/hooks/use-form-watcher/use-form-watcher-constants';
import {CustomSelect} from 'components/form/inputs/custom-select';

import {
    useAutoAddOnEmpty
} from '../../hooks/use-auto-add-on-empty';
import {
    FIN_SOURCE_TYPE_CODES_FOR_INN, FIN_SOURCE_TYPE_CODES_FOR_REG_NUM,
} from '../../model/project-investment-info-item-modal.constants';
import {IProjectInfluenceItemForm} from '../../model/project-investment-info-item-modal.types';
import {lengthValidationRule, requiredInputRule} from '../../utils';
import {FinancialSourcesYearItem} from './financial-sources-year-item';

interface IFinancialSourcesProps {
    field: FormListFieldData;
    onRemove: (index: number) => void;
}

interface IFinancialSourcesInnerProps {
    fields: any[];
    add: () => void;
    remove: (index: number) => void;
}

const FinancialSourcesInner = memo<IFinancialSourcesInnerProps>(
    ({fields, add, remove} : IFinancialSourcesInnerProps) => {

    const {handleAdd} = useAutoAddOnEmpty(fields.length, add);

    return (
        <>
            <Badge count={fields.length} className="add-project-badge">
                <Button
                    type="primary"
                    onClick={handleAdd}
                    icon={<PlusOutlined />}
                    className="add-project-button"
                >
                    Добавить год финансирования
                </Button>
            </Badge>

            {fields.map((yearField) => (
                <FinancialSourcesYearItem
                    key={yearField.key}
                    field={yearField}
                    onRemove={remove}
                />
            ))}
        </>
    );
});

export const FinancialSources: FC<IFinancialSourcesProps> = ({field, onRemove}: IFinancialSourcesProps) => (
        <div className="project-item-block">
            <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onRemove(field.name)}
                className="delete-button"
            />
            <div className="form-flex-row">
                <Form.Item
                    name={[field.name, 'finSourceType']}
                    label="Источники финансирования"
                    rules={[requiredInputRule()]}
                >
                    <CustomSelect
                        settings={{
                            placeholder: 'Выберите вариант ответа',
                            url: 'valueLists/FLEX_VALUE?sublistCode=fin_source_type',
                            valuePath: 'lookupCode',
                            labelPath: 'description',
                        }}
                    />
                </Form.Item>
                <Form.Item
                    name={[field.name, 'finSourceDescription']}
                    label="Наименование источника финансирования"
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <Form.Item
                    name={[field.name, 'finSourceName']}
                    label="Наименование финансирующей организации"
                    rules={[requiredInputRule()]}
                >
                    <Input placeholder="Введите значение" autoComplete="off" />
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues: IProjectInfluenceItemForm, currentValues: IProjectInfluenceItemForm) => {
                        const prevInvestStatus = prevValues.finSources
                            ? prevValues.finSources[field.name]?.finSourceType
                            : undefined;
                        const currentInvestStatus = currentValues.finSources
                            ? currentValues.finSources[field.name]?.finSourceType
                            : undefined;
                        return prevInvestStatus !== currentInvestStatus;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const sourceType = getFieldValue(['finSources', field.name, 'finSourceType']);
                        const isActive = sourceType && FIN_SOURCE_TYPE_CODES_FOR_INN.includes(sourceType);

                        return (
                            <Form.Item
                                name={[field.name, 'finSourceInn']}
                                label="ИНН финансирующей организации"
                                rules={[
                                    isActive ? requiredInputRule() : {},
                                    lengthValidationRule({comparisonOperator: EComparisonType.GREATER, value: 10}),
                                    lengthValidationRule({comparisonOperator: EComparisonType.LESS, value: 12}),
                                ]}
                            >
                                <Input disabled={!isActive} placeholder="Введите значение" autoComplete="off" />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
                <Form.Item
                    shouldUpdate={(prevValues: IProjectInfluenceItemForm, currentValues: IProjectInfluenceItemForm) => {
                        const prevInvestStatus = prevValues.finSources
                            ? prevValues.finSources[field.name]?.finSourceType
                            : undefined;
                        const currentInvestStatus = currentValues.finSources
                            ? currentValues.finSources[field.name]?.finSourceType
                            : undefined;
                        return prevInvestStatus !== currentInvestStatus;
                    }}
                    noStyle
                >
                    {({getFieldValue}) => {
                        const sourceType = getFieldValue(['finSources', field.name, 'finSourceType']);
                        const isActive = sourceType && FIN_SOURCE_TYPE_CODES_FOR_REG_NUM.includes(sourceType);

                        return (
                            <Form.Item
                                name={[field.name, 'finSourceForeignRegNumber']}
                                label="Регистрационный номер в стране регистрации (инкорпорации)"
                                rules={[
                                    isActive ? requiredInputRule() : {},
                                ]}
                            >
                                <Input disabled={!isActive} placeholder="Введите значение" autoComplete="off" />
                            </Form.Item>
                        );
                    }}
                </Form.Item>
            </div>
            <div className="form-flex-row">
                <div
                    className="form-flex"
                    style={{
                        maxHeight: 'calc(min(600px, 64vh))',
                        overflow: 'auto',
                        paddingRight: 8,
                        width: '100%',
                    }}
                >
                    <Form.List name={[field.name, 'finSourceValues']} >
                        {(fields, {add, remove}) => (
                            <FinancialSourcesInner
                                fields={fields}
                                add={add}
                                remove={remove}
                            />
                        )}
                    </Form.List>
                </div>
            </div>
        </div>
    );
